/**
 * @copyright 2026 Cooltrax Pty Ltd. All rights reserved.
 *
 * The list as spreadsheet rows, for the toolbar's download.
 */

import { convertCelsius, tempUnitSuffix } from '@/utils/conversions'

import { type Units, doorLabel, formatTiming, isOfflineTemperature } from './format'
import type { InStoreAsset } from './types'

export type ExportRow = Record<string, string | number | null>

/** The columns, by row key, with their headings in the user's units. */
export const exportColumns = (units: Units, showTiming: boolean): Record<string, string> => ({
  location: 'Location',
  sensor: 'Sensor',
  type: 'Type',
  temperature: `Temperature (${tempUnitSuffix(units)})`,
  door: 'Door',
  ...(showTiming ? { timeInRange: 'Time in range' } : {}),
  humidity: 'Humidity (%)',
  online: 'Online',
  alerts: 'Active alerts',
})

/** One row per sensor the list shows, under its location, readings in the user's units. */
export const exportRows = (locations: InStoreAsset[], units: Units, showTiming: boolean, now: number): ExportRow[] =>
  locations.flatMap((asset) =>
    asset.sensors.map((sensor) => {
      const isDoor = sensor.type === 'door'
      const timing = showTiming && !isDoor ? formatTiming(sensor.timing, now) : undefined
      const offline = isOfflineTemperature(sensor)
      const fault = !isDoor && !offline && sensor.tagState != null && sensor.tagState !== 'ok'
      return {
        location: asset.name,
        sensor: sensor.name,
        type: isDoor ? 'Door' : 'Temperature',
        temperature:
          isDoor || offline
            ? null
            : fault
              ? 'Fault'
              : sensor.temperatureCelsius != null
                ? Number(convertCelsius(sensor.temperatureCelsius, units).toFixed(1))
                : null,
        door: isDoor ? doorLabel(sensor) : null,
        ...(showTiming ? { timeInRange: timing && timing.text !== '—' ? timing.text : null } : {}),
        humidity: sensor.humidity?.enabled ? sensor.humidity.percent : null,
        online: sensor.online == null ? null : sensor.online ? 'Yes' : 'No',
        alerts: sensor.activeAlerts.map((alert) => alert.name).join(', ') || null,
      }
    })
  )
